import clsx from "clsx";
import { ChevronDown, Search, TriangleAlert, X } from "lucide-react";
import type { ReactNode } from "react";
import type { TagRow } from "./meta";
import { ListWarning } from "../../web/components/ui";

/*
 * The top of the Price List's lists (items, bundles, suppliers, groups), the same in each (FilterBar): a search (every word,
 * anywhere: "door closer"), then which ones as tabs with their counts (In use, For sale, Hidden, Archived, All), then what needs
 * attention among them, a chip for each kind with how many (as a window's "Needs attention" box has them): pressing one narrows the
 * list to those, pressing it again shows them all. Anything else to narrow by (a tag, setting up) is a small pill beside the search.
 * Rows that need attention are flagged in the list itself too (NeedsAttention, is-warning).
 */

/** Whether `text` has every word of a search, in any order and case. */
export function matchesSearch(text: string, query: string) {
    const lower = text.toLowerCase();
    return query
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .every((w) => lower.includes(w));
}

/** One of a list's tabs: which ones it shows ("In use"), and how many. */
export interface FilterTab<T extends string> {
    id: T;
    label: string;
    count: number;
}

/** One kind of what needs attention in a list ("Price to update"), how many have it, and red when it can cost money or trip up accounting. */
export interface FilterKind<K extends string> {
    id: K;
    label: string;
    count: number;
    danger?: boolean;
}

/** Narrowed to every one needing attention (the "All" chip), not one kind. */
export const ANY_KIND = "any";

export function FilterBar<T extends string, K extends string>(
    props: Readonly<{
        /** What it searches, for screen readers ("Search items"), and the hint in the box. */
        searchLabel: string;
        placeholder: string;
        query: string;
        onQuery: (q: string) => void;
        /** Which ones it shows, as tabs (none, or just one: no tabs). */
        tabs?: readonly FilterTab<T>[];
        tab?: T;
        onTab?: (tab: T) => void;
        /** What needs attention among those the tab shows: a chip for each kind there is (none: no row), and `anyCount` how many have any. */
        kinds?: readonly FilterKind<K>[];
        anyCount?: number;
        /** The kind it's narrowed to (ANY_KIND: any of them), or null. */
        kind?: K | typeof ANY_KIND | null;
        onKind?: (kind: K | typeof ANY_KIND | null) => void;
        /** Beside the search: more to narrow by (TagFilter, PillSelect, a pill saying it's narrowed to something). */
        children?: ReactNode;
    }>,
) {
    const kinds = (props.kinds ?? []).filter((k) => k.count > 0 || k.id === props.kind);
    const pick = (k: K | typeof ANY_KIND) => props.onKind?.(props.kind === k ? null : k);
    return (
        <div className="border-b border-line">
            <div className="flex flex-wrap items-center gap-2 px-5 pt-3">
                <label className="relative min-w-60 flex-1">
                    <span className="sr-only">{props.searchLabel}</span>
                    <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" />
                    <input
                        type="text"
                        className="input pr-9 pl-9"
                        placeholder={props.placeholder}
                        value={props.query}
                        onChange={(e) => props.onQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Escape" && props.onQuery("")}
                    />
                    {props.query ? (
                        <button type="button" className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-muted hover:text-ink" aria-label="Clear the search" onClick={() => props.onQuery("")}>
                            <X className="size-3.5" />
                        </button>
                    ) : null}
                </label>
                {props.children}
            </div>
            {/* Which ones: tabs with their counts, as ServiceM8's own lists split them. */}
            {props.tabs && props.tabs.length > 1 ? (
                <div className="flex flex-wrap gap-1 px-4 pt-2" role="tablist">
                    {props.tabs.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={t.id === props.tab}
                            className={clsx(
                                "-mb-px flex items-center gap-1.5 border-b-2 px-2.5 py-2 text-sm whitespace-nowrap transition",
                                t.id === props.tab ? "border-brand-500 font-semibold text-ink" : "border-transparent text-muted hover:text-ink",
                            )}
                            onClick={() => props.onTab?.(t.id)}
                        >
                            {t.label}
                            <span className={clsx("rounded-full px-1.5 text-xs tabular-nums", t.id === props.tab ? "bg-brand-500/15 text-brand-ink" : "bg-surface-2 text-muted")}>{t.count.toLocaleString()}</span>
                        </button>
                    ))}
                </div>
            ) : (
                <div className="pt-3" />
            )}
            {/* What needs attention among them: All, then each kind, pressed to narrow (again to show them all). */}
            {kinds.length ? (
                <div className="flex flex-wrap items-center gap-1.5 border-t border-line bg-warning-soft/40 px-5 py-2">
                    <span className="mr-1 text-xs font-semibold text-warning-ink">Needs attention</span>
                    <KindChip label={`All ${(props.anyCount ?? 0).toLocaleString()}`} danger={kinds.some((k) => k.danger)} pressed={props.kind === ANY_KIND} onClick={() => pick(ANY_KIND)} />
                    {kinds.map((k) => (
                        <KindChip key={k.id} label={`${k.count.toLocaleString()} ${k.label}`} danger={k.danger} pressed={props.kind === k.id} onClick={() => pick(k.id)} />
                    ))}
                </div>
            ) : null}
        </div>
    );
}

/** A kind of what needs attention, as a window's chips look (IssueChip): pressed, filled, with its × to show them all again. */
function KindChip(props: Readonly<{ label: string; danger?: boolean; pressed: boolean; onClick: () => void }>) {
    return (
        <button
            type="button"
            aria-pressed={props.pressed}
            className={clsx("issue-chip", props.danger && "is-error", props.pressed && (props.danger ? "bg-danger-soft" : "bg-warning-tint"))}
            title={props.pressed ? "Show them all again" : `Show just these: ${props.label}`}
            onClick={props.onClick}
        >
            <TriangleAlert className="size-3.5 shrink-0" />
            {props.label}
            {props.pressed ? <X className="size-3.5 shrink-0" /> : null}
        </button>
    );
}

/**
 * A small dropdown beside a list's search, as a pill ("Tag: Any ▾"): what it narrows by and what's chosen, outlined in green when
 * something is. `options` with their counts, if given.
 */
export function PillSelect(
    props: Readonly<{ label: string; value: string; anyLabel?: string; options: readonly { value: string; label: string }[]; onChange: (value: string) => void; className?: string }>,
) {
    const chosen = props.options.find((o) => o.value === props.value);
    return (
        <label
            className={clsx(
                "relative inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full border px-3 py-1 text-sm whitespace-nowrap",
                chosen ? "border-brand-500 bg-brand-500/10 text-ink" : "border-line text-muted hover:text-ink",
                props.className,
            )}
        >
            {props.label}: <span className={chosen ? "font-semibold" : undefined}>{chosen ? chosen.label : (props.anyLabel ?? "Any")}</span>
            <ChevronDown className="size-3.5" />
            <select className="absolute inset-0 cursor-pointer opacity-0" aria-label={props.label} value={chosen ? props.value : ""} onChange={(e) => props.onChange(e.target.value)}>
                <option value="">{props.anyLabel ?? "Any"}</option>
                {props.options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
        </label>
    );
}

/** Narrowing a list to one tag's items (or bundles), the same in each list (PillSelect). None while there are no tags. */
export function TagFilter({ tags, value, onChange, className }: Readonly<{ tags: readonly TagRow[]; value: string; onChange: (tagId: string) => void; className?: string }>) {
    if (!tags.length) return null;
    return <PillSelect label="Tag" value={value} options={tags.map((t) => ({ value: t.id, label: t.name }))} onChange={onChange} className={className} />;
}

/** A pill saying a list is narrowed to something from elsewhere (a group's items, just some): its × shows them all again. */
export function NarrowedPill({ children, title, onClear }: Readonly<{ children: ReactNode; title?: string; onClear: () => void }>) {
    return (
        <button type="button" className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-brand-500 bg-brand-500/10 px-3 py-1 text-sm text-ink" title={title ?? "Show them all again"} onClick={onClear}>
            {children} <X className="size-3.5 text-muted" />
        </button>
    );
}

/**
 * What needs attention on a row, as one mark after its name (a count when more than one; hover lists
 * them, worst first): the same in every list. Nothing when there's nothing.
 */
export function NeedsAttention({ problems, danger = false }: Readonly<{ problems: readonly string[]; danger?: boolean }>) {
    if (!problems.length) return null;
    return (
        <ListWarning tone={danger ? "danger" : "warning"} title={`Needs attention:\n${problems.map((x) => `• ${x}`).join("\n")}\nOpen it to see more.`}>
            {problems.length > 1 ? problems.length.toLocaleString() : undefined}
        </ListWarning>
    );
}
